import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Badge, Card } from 'react-bootstrap';
import ApiService from '../../../helpers/apiService';
import AccountsFilters from './AccountsFilters';
import AccountsTable from './AccountsTable';
import { IAccount } from './types';

const LIST_PAGE_SIZE = 100;

const Accounts: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => {
    const q = new URLSearchParams(window.location.search).get('search');
    return { search: q ?? '', currencies: '', from: '', to: '' };
  });
  const [accountsFromApi, setAccountsFromApi] = useState<IAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    countAll: 0
  });

  const matchesSearch = (acc: IAccount, search: string): boolean => {
    if (!search || !search.trim()) return true;
    const s = search.trim().toLowerCase();
    const account = (acc.account ?? '').toString().toLowerCase();
    const name = (acc.name ?? '').toString().toLowerCase();
    const currency = (acc.currency ?? '').toString().toLowerCase();
    return account.includes(s) || name.includes(s) || currency.includes(s);
  };

  const filteredAccounts = useMemo(() => {
    const search = (filters.search ?? '').trim();
    if (!search) return accountsFromApi;
    return accountsFromApi.filter((acc) => matchesSearch(acc, search));
  }, [accountsFromApi, filters.search]);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const response = await ApiService.searchAccounts({
        search: '',
        currency: filters.currencies,
        dateFrom: filters.from,
        dateTo: filters.to,
        page: 1,
        limit: LIST_PAGE_SIZE
      });
      const data = response;
      const list = Array.isArray(data?.data) ? data.data : [];
      setAccountsFromApi(list);
      setPagination(prev => ({ ...prev, page: 1 }));
    } catch (error) {
      console.error('Ошибка при загрузке счетов:', error);
      setAccountsFromApi([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterSubmit = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchAccounts();
  };

  const totalFiltered = filteredAccounts.length;
  const paginatedAccounts = useMemo(() => {
    const { page, limit } = pagination;
    const start = (page - 1) * limit;
    return filteredAccounts.slice(start, start + limit);
  }, [filteredAccounts, pagination.page, pagination.limit]);

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const paginationForTable = useMemo(() => ({
    page: pagination.page,
    limit: pagination.limit,
    countAll: totalFiltered
  }), [pagination.page, pagination.limit, totalFiltered]);

  // Поиск по счёту из URL (переход из Транзакций по клику на отправителя)
  useEffect(() => {
    const searchFromUrl = searchParams.get('search');
    if (searchFromUrl != null) {
      setFilters(prev => ({ ...prev, search: searchFromUrl }));
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  }, [searchParams]);

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [filters.search]);

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <h3 className="mb-0">Счета</h3>
              <p className="mt-1 mb-0 text-muted small">Список счетов пользователей</p>
            </div>
            <Badge bg="secondary" style={{ fontSize: '0.8rem', padding: '6px 10px' }}>
              Найдено: {totalFiltered}
            </Badge>
          </div>
        </Card.Body>
      </Card>
      <Card>
        <Card.Body>
          <AccountsFilters
            values={filters}
            setFieldValue={(field, value) => setFilters(prev => ({ ...prev, [field]: value }))}
            handleSubmit={handleFilterSubmit}
            loading={loading}
          />
          <AccountsTable
            accounts={paginatedAccounts}
            pagination={paginationForTable}
            handlePageChange={handlePageChange}
          />
        </Card.Body>
      </Card>
    </>
  );
};

export default Accounts;
